import { useQuery } from "@apollo/client"
import { useEffect, useState } from "react"
import invariant from "tiny-invariant"
import { getFragmentData } from "~/__generated__"
import { useDocumentTitle } from "~/common/useDocumentTitle"
import { usePageVisibility } from "~/contexts/PageVisibilityContext"
import { ErrorBox } from "~/ui/Error"
import { LoadingIndicatorCentered } from "~/ui/LoadingIndicator"
import { AnalyticsSource } from "~/util/analyticsEvents"
import { trackEvent } from "~/util/tracking"
import {
  FULL_INFLUENCER_FRAGMENT,
  FULL_INFLUENCER_QUERY_DOCUMENT,
} from "../api"
import { InfluencerTabBar, Tab } from "../components/InfluencerTabBar"
import { DetailsTab } from "./components/DetailsTab"
import { Heading } from "./components/Heading"
import { InfluencerActions } from "./components/InfluencerActions"
import { NotesTab } from "./components/NotesTab"
import { PersonalInformationTab } from "./components/PersonalInformationTab"
import { SocialMediaTab } from "./components/SocialMediaTab"

export const ViewInfluencerScreen = ({
  influencerId,
  actionable = false,
  onEditClick,
  onDeleteClick,
  initialTab = Tab.SocialMedia,
  source = "roster_database",
}: {
  influencerId: string
  actionable?: boolean
  onEditClick?: (currentTab: Tab) => void
  onDeleteClick?: () => void
  initialTab?: Tab
  source?: AnalyticsSource
}) => {
  const [currentTab, setCurrentTab] = useState(initialTab)
  const { data, loading, error } = useQuery(FULL_INFLUENCER_QUERY_DOCUMENT, {
    variables: { influencerId },
  })
  const influencer = getFragmentData(FULL_INFLUENCER_FRAGMENT, data?.influencer)
  const isPublic = usePageVisibility() === "public"

  useDocumentTitle(
    isPublic && influencer?.name
      ? `${influencer.name} Media Kit | Represented by ${
          influencer.agency?.name ?? ""
        }`
      : influencer?.name || "Influencer",
    { raw: isPublic && !!influencer?.name, ready: !loading }
  )

  const influencerName = influencer?.name ?? null
  const agencyName = influencer?.agency?.name ?? null
  useEffect(() => {
    if (!isPublic || !influencerName || !agencyName) return
    const content = `${influencerName} is represented exclusively by ${agencyName}, see full metrics and audience demographics on Zano`
    const existing = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]'
    )
    const previousContent = existing?.getAttribute("content")
    const meta =
      existing ??
      (() => {
        const el = document.createElement("meta")
        el.setAttribute("name", "description")
        document.head.appendChild(el)
        return el
      })()
    meta.setAttribute("content", content)
    return () => {
      if (previousContent === null || previousContent === undefined) {
        meta.remove()
      } else {
        meta.setAttribute("content", previousContent)
      }
    }
  }, [isPublic, influencerName, agencyName])

  useEffect(() => {
    if (influencer) {
      trackEvent("view_influencer", {
        influencerId: influencer.id ?? "",
        influencerName: influencer.name,
        source,
      })
    }
  }, [influencer, source])

  const onSelectTab = (tab: Tab) => {
    setCurrentTab(tab)
    trackEvent("view_influencer_tab", { tab, influencerId })
  }

  if (!data && loading) return <LoadingIndicatorCentered />
  if (error || !influencer)
    return <ErrorBox message={`Error loading influencer ${influencerId}`} />

  let actionBar = null
  if (actionable) {
    invariant(onEditClick, "onEditClick is required")
    invariant(onDeleteClick, "onDeleteClick is required")
    // canUpdate is per-record: this screen is also reached from the roster
    // database and public roster, where the influencer belongs to another
    // agency. Only the write affordances are gated on it — the Media Kit
    // button inside is a read action and stays available.
    actionBar = (
      <InfluencerActions
        influencer={influencer}
        onEditClick={onEditClick}
        onDeleteClick={onDeleteClick}
        currentTab={currentTab}
        canManage={influencer.canUpdate.value}
      />
    )
  }

  return (
    <div className="absolute top-0 bottom-0 left-0 right-0 lg:px-4 lg:pb-4 lg:pt-5 flex flex-shrink flex-col">
      <div>
        <Heading influencer={influencer} />
        {actionBar}
        <InfluencerTabBar currentTab={currentTab} setCurrentTab={onSelectTab} />
      </div>
      <div className="overflow-y-auto">
        {currentTab === Tab.Details && <DetailsTab influencer={influencer} />}
        {currentTab === Tab.PersonalInformation && (
          <PersonalInformationTab influencer={influencer} />
        )}
        {currentTab === Tab.SocialMedia && (
          <SocialMediaTab influencer={influencer} />
        )}
        {currentTab === Tab.Notes && <NotesTab influencer={influencer} />}
      </div>
    </div>
  )
}
